/**
 * Print Continuity / Year Map — Canva page-14.
 * YearRadialMap donut (Past / Current / Ahead) plus a compact 3×4 palace grid.
 * Prior-month bridge is omitted here so the sheet stays one A4; Letter shows it.
 */

import React from "react";
import type { LucideIcon } from "lucide-react";
import {
  CalendarDays,
  Coins,
  Heart,
  HeartPulse,
  Home,
  Leaf,
  Plane,
  RefreshCw,
  Shield,
  TrendingUp,
  Users,
} from "lucide-react";
import type { MonthlyConsultationBundle } from "../../../../utils/forecast/monthlyConsultation";
import { liuSeasonShort } from "../../../../utils/forecast/monthlyConsultation/englishLabels";
import { YearRadialMap, YEAR_RADIAL_MAP_PDF_SIZE } from "../../shared/YearRadialMap";
import { PdfChapterShell } from "../atoms";
import {
  clipPdfText,
  yearTimelineStatus,
} from "../pdfCopy";
import type { YearTimelineStatus } from "../pdfCopy";
import {
  bodyFontFamily,
  border,
  cardWhite,
  charcoal,
  displayFontFamily,
  mutedGray,
  palePink,
  tanGold,
  terracotta,
} from "../pdfTheme";

/**
 * Props for the PDF continuity chapter.
 */
export interface PdfChapterContinuityProps {
  /** Full monthly consultation content bundle. */
  bundle: MonthlyConsultationBundle;
  /** 1-based sequential page index among rendered sheets. */
  pageNumber?: number;
}

/**
 * Thin gold rule with a diamond at the right end.
 *
 * @param props - Stroke color
 * @returns Rule row
 */
const PdfDiamondRule: React.FC<{ readonly color: string }> = ({ color }) => {
  const stroke =
    typeof color === "string" && color.trim().length > 0
      ? color.trim()
      : tanGold;
  return (
    <div
      aria-hidden="true"
      style={{
        display: "flex",
        alignItems: "center",
        width: "100%",
      }}
    >
      <span
        style={{
          flex: 1,
          height: 1,
          background: stroke,
        }}
      />
      <span
        style={{
          width: 6,
          height: 6,
          background: stroke,
          transform: "rotate(45deg)",
          flexShrink: 0,
          marginLeft: 1,
        }}
      />
    </div>
  );
};

/**
 * Lucide icon for an English palace label.
 *
 * @param palaceName - Palace display name
 * @returns Matching icon
 */
function palaceIcon(palaceName: string): LucideIcon {
  const key = typeof palaceName === "string" ? palaceName.trim().toLowerCase() : "";
  if (key.includes("wealth") || key.includes("money")) {
    return Coins;
  }
  if (key.includes("child")) {
    return Shield;
  }
  if (key.includes("spouse") || key.includes("marriage") || key.includes("partner")) {
    return Heart;
  }
  if (key.includes("sibling") || key.includes("friend") || key.includes("parent")) {
    return Users;
  }
  if (key.includes("life")) {
    return RefreshCw;
  }
  if (key.includes("wellbeing") || key.includes("fortune")) {
    return Leaf;
  }
  if (key.includes("property") || key.includes("house")) {
    return Home;
  }
  if (key.includes("career") || key.includes("official")) {
    return TrendingUp;
  }
  if (key.includes("travel") || key.includes("migration")) {
    return Plane;
  }
  if (key.includes("health") || key.includes("body")) {
    return HeartPulse;
  }
  return CalendarDays;
}

/**
 * Dot / pill color for a timeline status.
 *
 * @param status - PAST / CURRENT / AHEAD
 * @returns Theme hex
 */
function statusColor(status: YearTimelineStatus): string {
  if (status === "CURRENT") {
    return terracotta;
  }
  if (status === "PAST") {
    return mutedGray;
  }
  return tanGold;
}

/**
 * Year continuity — radial map plus 3×4 month grid. Current month highlighted.
 */
export const PdfChapterContinuity: React.FC<PdfChapterContinuityProps> = ({
  bundle,
  pageNumber,
}) => {
  const yearClimate = Array.isArray(bundle.yearClimate)
    ? bundle.yearClimate
    : [];
  const currentMonth =
    typeof bundle.lunarMonth === "number" && Number.isFinite(bundle.lunarMonth)
      ? Math.trunc(bundle.lunarMonth)
      : 1;

  return (
    <PdfChapterShell
      chapterId="continuity"
      eyebrow=""
      title=""
      hideHeader={true}
      breakBefore="always"
      fillPage={true}
      compact
      pageNumber={pageNumber}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          fontFamily: bodyFontFamily,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            marginBottom: 8,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: `${tanGold}22`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              marginTop: 2,
            }}
          >
            <CalendarDays size={18} color={terracotta} strokeWidth={1.8} />
          </span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <p
              style={{
                margin: 0,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: terracotta,
              }}
            >
              + YEAR VIEW
            </p>
            <h2
              style={{
                margin: "2px 0 0",
                fontFamily: displayFontFamily,
                fontSize: 24,
                fontWeight: 700,
                lineHeight: 1.15,
                letterSpacing: "-0.02em",
                color: charcoal,
              }}
            >
              The Year Map
            </h2>
            <p
              style={{
                margin: "6px 0 0",
                fontSize: 12,
                lineHeight: 1.45,
                color: mutedGray,
              }}
            >
              Twelve lunar months for this year. Current month is highlighted.
            </p>
          </div>
        </div>
        <div style={{ marginBottom: 8 }}>
          <PdfDiamondRule color={tanGold} />
        </div>

        {yearClimate.length > 0 ? (
          <div
            className="mc-pdf-year-radial-wrap"
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: 12,
            }}
          >
            <YearRadialMap
              months={yearClimate}
              size={YEAR_RADIAL_MAP_PDF_SIZE}
              className="mc-pdf-year-radial flex flex-col items-center gap-3"
            />
          </div>
        ) : (
          <p
            style={{
              margin: "0 0 10px",
              fontSize: 12,
              color: mutedGray,
            }}
          >
            Year climate rows are not available for this report.
          </p>
        )}

        {yearClimate.length > 0 ? (
          <div className="mc-pdf-year-month-grid">
            {yearClimate.map((row) => {
              const season = liuSeasonShort(row.season);
              const status = yearTimelineStatus(
                row.isCurrent === true,
                row.lunarMonth,
                currentMonth
              );
              const accent = statusColor(status);
              const Icon = palaceIcon(row.palaceNameEnglish);
              const palace =
                typeof row.palaceNameEnglish === "string"
                  ? clipPdfText(row.palaceNameEnglish, 22)
                  : "";
              const isCurrent = status === "CURRENT";
              return (
                <div
                  key={`m-${String(row.lunarMonth)}`}
                  className={`mc-pdf-card mc-pdf-year-month-card${isCurrent ? " mc-pdf-year-month-card--current" : ""}`}
                  style={{
                    background: isCurrent ? palePink : cardWhite,
                    border: isCurrent
                      ? `1.5px solid ${terracotta}`
                      : `1px solid ${border}`,
                    fontFamily: bodyFontFamily,
                  }}
                >
                  <span
                    aria-hidden="true"
                    className="mc-pdf-year-month-icon"
                    style={{ color: accent }}
                  >
                    <Icon size={36} color={accent} strokeWidth={1.5} />
                  </span>
                  <div className="mc-pdf-year-month-head">
                    <p className="mc-pdf-year-month-label">{`M${String(row.lunarMonth)}`}</p>
                    <span
                      className="mc-pdf-year-month-dot"
                      style={{ background: accent }}
                      aria-hidden="true"
                    />
                  </div>
                  <p className="mc-pdf-year-month-palace">{palace}</p>
                  <p className="mc-pdf-year-month-season">{season}</p>
                  <p
                    className="mc-pdf-year-month-status"
                    style={{ color: accent }}
                  >
                    {status}
                  </p>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </PdfChapterShell>
  );
};

export default PdfChapterContinuity;
